import { useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { EmptyState } from "@/components/common/EmptyState";
import { ProcessStatesInfo } from "@/components/common/ProcessStatesInfo";
import { useWidth } from "@/hooks/useWidth";
import {
  MoreHorizontal,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Info,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuth } from "@/contexts/AuthContext";
import { getPaymentCountdown } from "@/utils/paymentCountdown";

export type TabType = "all" | "lead" | "active" | "on_hold" | "inactive" | "transferred";

// Condiciones que implica marcar un paciente como urgente — orden de más a menos crítico.
// Compartido entre el tooltip informativo y el checklist del modal de confirmación.
export const URGENT_CONDITIONS = [
  "Puede tener sesiones antes de completar el pago",
  "Podés ver su número de teléfono para contacto directo",
  "Puede agendar sesiones sin los tiempos habituales de anticipación",
];

export interface Patient {
  id: string;
  processId?: string;
  name: string;
  email?: string;
  service?: string;
  location?: string;
  nationality?: string;
  seniority?: string;
  firstSession?: string;
  lastSession?: string;
  lastSessionDate?: string | null;   // ISO date for tooltip calculations
  nextSession?: string | null;
  nextSessionDate?: string | null;   // ISO date for calendar navigation
  totalSessions: number;
  patientStatus?: "lead" | "active" | "on_hold" | "inactive" | "transferred";
  paymentMethod?: "automatic" | "manual";
  paymentStatus?: "pending" | "paid";
  rawPaymentStatus?: string;
  consultationReason?: string;
  isReferred?: boolean;
  isUrgent?: boolean;
  urgentMarkedAt?: string;
  schedulingEligible?: boolean;
}

interface PatientTableProps {
  activeTab: TabType;
  currentPage: number;
  onPageChange: (page: number) => void;
  itemsPerPage?: number;
  patients: Patient[];
  loading?: boolean;
  onViewPatient?: (patient: Patient) => void;
  onViewPatientDerivation?: (patient: Patient) => void;
  onReadConsultation?: (reason: string | undefined, patient: Patient) => void;
  onOpenNoSchedule?: (patient: Patient) => void;
  onOpenRefer?: (patient: Patient) => void;
  onScheduleFromList?: (patient: Patient) => void;
  onGoToCalendar?: (patient: Patient) => void;
  onAddNote?: (patient: Patient) => void;
  onViewNotes?: (patient: Patient) => void;
  onMarkUrgent?: (patient: Patient) => void;
  onUnmarkUrgent?: (patient: Patient) => void;
  /** NZ, u O que viene de NZ: no puede iniciar derivaciones hasta validar con Ops. */
  disableReferForm?: boolean;
}

const REFER_DISABLED_TOOLTIP = "Por favor contactate con psicos@psimammoliti.com para validar la derivación primero";

type SortableKey =
  | "name"
  | "nextSession"
  | "lastSession"
  | "firstSession"
  | "totalSessions"
  | "seniority"
  | "patientStatus";

// Etiqueta en español de cada patientStatus — misma fuente que statusBadge, reutilizada
// acá para poder ordenar la columna "Estado" alfabéticamente por lo que el usuario ve
// (el label), no por el valor crudo interno ("on_hold", "transferred", etc).
const STATUS_LABELS: Record<NonNullable<Patient["patientStatus"]>, string> = {
  lead: "Nuevo",
  active: "Activo",
  on_hold: "En pausa",
  inactive: "Inactivo",
  transferred: "Derivación exitosa",
};

/**
 * Builds a Gmail web-compose URL instead of a plain `mailto:` link, so clicking
 * the patient's email opens Gmail directly with the psicóloga's psimammoliti.com
 * account pre-selected as sender (via `authuser`) instead of the OS's default mail app.
 */
export function buildGmailComposeUrl(to: string, authuserEmail?: string): string {
  const params = new URLSearchParams({ view: "cm", fs: "1", to });
  if (authuserEmail) params.set("authuser", authuserEmail);
  return `https://mail.google.com/mail/?${params.toString()}`;
}

export function PatientTable({
  activeTab,
  currentPage,
  onPageChange,
  itemsPerPage = 8,
  patients,
  loading,
  onViewPatient,
  onViewPatientDerivation,
  onReadConsultation,
  onOpenNoSchedule,
  onOpenRefer,
  onScheduleFromList,
  onGoToCalendar,
  onAddNote,
  onViewNotes,
  disableReferForm,
  onMarkUrgent,
  onUnmarkUrgent,
}: PatientTableProps) {
  const { size } = useWidth();
  const isMobile = size === 's';
  const { user } = useAuth();

  const [sortKey, setSortKey] = useState<SortableKey | null>(null);

  // Modal explicativo de los estados de un proceso (se abre desde el header "Estado")
  const [statusInfoOpen, setStatusInfoOpen] = useState(false);

  // Urgent confirmation dialog state
  const [urgentConfirm, setUrgentConfirm] = useState<{ patient: Patient; action: 'mark' | 'unmark' } | null>(null);
  // Un checkbox por cada condición de URGENT_CONDITIONS, en el mismo orden
  const [markConditions, setMarkConditions] = useState<boolean[]>(() => URGENT_CONDITIONS.map(() => false));
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const colCount =
    activeTab === "active" ? 9 :
    activeTab === "all" ? 8 :
    activeTab === "lead" ? 7 :
    5; // inactive / on_hold / transferred

  const toggleSort = (key: SortableKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
    onPageChange(1);
  };

  const sortedPatients = useMemo(() => {
    if (!sortKey) return patients;
    return [...patients].sort((a, b) => {
      if (sortKey === "totalSessions") {
        const n = (a.totalSessions || 0) - (b.totalSessions || 0);
        return sortDir === "asc" ? n : -n;
      }
      if (sortKey === "patientStatus") {
        const av = a.patientStatus ? STATUS_LABELS[a.patientStatus] : "";
        const bv = b.patientStatus ? STATUS_LABELS[b.patientStatus] : "";
        const cmp = av.localeCompare(bv, "es");
        return sortDir === "asc" ? cmp : -cmp;
      }
      const av = String(a[sortKey] ?? "");
      const bv = String(b[sortKey] ?? "");
      const cmp = av.localeCompare(bv, "es");
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [patients, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sortedPatients.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const pagePatients = sortedPatients.slice(startIndex, startIndex + itemsPerPage);

  const sortIcon = (col: SortableKey) => {
    if (sortKey !== col)
      return <ChevronsUpDown size={13} className="ml-1 opacity-40 flex-shrink-0" />;
    return sortDir === "asc" ? (
      <ChevronUp size={13} className="ml-1 flex-shrink-0" />
    ) : (
      <ChevronDown size={13} className="ml-1 flex-shrink-0" />
    );
  };

  const sortHead = (col: SortableKey, label: string, className?: string) => (
    <TableHead
      key={col}
      className={`text-primary font-semibold cursor-pointer select-none hover:bg-primary/5 transition-colors ${className ?? ""}`}
      onClick={() => toggleSort(col)}
    >
      <span className="inline-flex items-center">
        {label}
        {sortIcon(col)}
      </span>
    </TableHead>
  );

  const daysSinceLastSession = (isoDate?: string | null): number | null => {
    if (!isoDate) return null;
    const diff = Math.floor((Date.now() - new Date(isoDate).getTime()) / (1000 * 60 * 60 * 24));
    return diff >= 0 ? diff : null;
  };

  // Único punto de verdad para "¿este proceso Inactivo ya está realmente frío?" — más de
  // 60 días sin sesión. Se usa tanto para la celda "Próxima sesión" (chip "Agendar" vs "—")
  // como para el botón "..." de acciones, así las dos reglas nunca quedan desalineadas.
  // El bucket "Inactivo" del backend no garantiza por sí solo los 60 días (puede haber
  // procesos recién marcados Inactivo con sesiones recientes), así que no alcanza con
  // confiar en `patientStatus` — hace falta el cálculo real de días.
  const isStaleInactiveProcess = (patient: Patient): boolean => {
    if (patient.patientStatus !== "inactive") return false;
    const days = daysSinceLastSession(patient.lastSessionDate);
    return days === null || days > 60;
  };

  const nextSessionChip = (patient: Patient) => {
    if (!patient.nextSession) {
      // No next session → show "Agendar" only if patient has had at least one session,
      // this psicóloga is still eligible to schedule with them, el proceso no está
      // Inactivo hace más de 60 días, y el paciente no está "En pausa" (mientras dure la
      // pausa no corresponde ofrecer agendar). `schedulingEligible` solo no alcanza: el
      // backend no siempre lo manda, y por default (`?? true`) cae como "elegible" incluso
      // para inactivos viejos.
      if (
        patient.totalSessions === 0 ||
        patient.schedulingEligible === false ||
        isStaleInactiveProcess(patient) ||
        patient.patientStatus === "on_hold"
      ) {
        return <span className="text-muted-foreground text-sm">—</span>;
      }
      return (
        <button
          type="button"
          onClick={() => onScheduleFromList?.(patient)}
          className="inline-flex items-center gap-1.5 bg-primary/5 text-primary text-xs font-medium px-2.5 py-1 rounded-full border border-primary/30 hover:bg-primary/10 hover:border-primary/50 transition-colors whitespace-nowrap"
        >
          <Calendar size={11} />
          Agendar
        </button>
      );
    }
    // Has next session → clickable chip that navigates to calendar
    return (
      <button
        type="button"
        onClick={() => onGoToCalendar?.(patient)}
        title="Ver en agenda"
        className="inline-flex items-center gap-1.5 bg-violet-50 text-violet-700 text-xs font-medium px-2.5 py-1 rounded-full border border-violet-200 hover:bg-violet-100 hover:border-violet-300 transition-colors whitespace-nowrap cursor-pointer"
      >
        <Calendar size={11} />
        {patient.nextSession}
      </button>
    );
  };

  const totalSessionsBadge = (n: number) => (
    <span className="inline-flex items-center justify-center bg-primary/10 text-primary font-semibold text-sm w-8 h-8 rounded-full">
      {n}
    </span>
  );

  // Badges de estado del vínculo paciente-proceso (process_patient.status).
  // Mismo diseño y colores que el panel "Tasa de inactividad" (MetricsInactividadTab).
  const statusBadge = (patient: Patient) => {
    const status = patient.patientStatus;
    const days = daysSinceLastSession(patient.lastSessionDate);
    if (status === "lead") return (
      <Badge className="bg-cyan-100 text-cyan-700 text-xs font-medium">Nuevo</Badge>
    );
    if (status === "active") {
      const tooltip = days !== null
        ? `Tuviste sesiones hace ${days} ${days === 1 ? "día" : "días"}`
        : undefined;
      return (
        <Badge title={tooltip} className="bg-emerald-100 text-emerald-700 text-xs font-medium">Activo</Badge>
      );
    }
    if (status === "on_hold") return (
      <Badge className="bg-amber-100 text-amber-700 text-xs font-medium">En pausa</Badge>
    );
    if (status === "inactive") {
      const tooltip = patient.totalSessions === 0
        ? "Aún no tuviste sesiones con este paciente"
        : days !== null
          ? `El paciente lleva sin agendar más de ${days} ${days === 1 ? "día" : "días"}`
          : "El paciente aún no tiene próxima sesión agendada";
      return (
        <Badge title={tooltip} className="bg-red-100 text-red-700 text-xs font-medium">Inactivo</Badge>
      );
    }
    if (status === "transferred") return (
      <Badge className="bg-blue-100 text-blue-700 text-xs font-medium">Derivación exitosa</Badge>
    );
    return <span className="text-muted-foreground text-sm">—</span>;
  };

  const statusCell = (patient: Patient) => (
    <div className="flex items-center gap-1 flex-wrap">
      {statusBadge(patient)}
    </div>
  );

  // Columna "Estado de pago" — independiente del estado de proceso (columna "Estado").
  // El tooltip reutiliza getPaymentCountdown, la misma utilidad que ya calcula el
  // countdown de pago en SessionRow / PatientDetailModal / SessionDetailsModal.
  const paymentStatusCell = (patient: Patient) => {
    if (patient.paymentStatus === "pending") {
      const badge = (
        <Badge
          data-testid="patient-payment-status-badge"
          className="bg-[#FAF7EE] text-[#B4700B] border border-[#e9c87a] rounded-full px-3 py-1 text-xs font-medium hover:bg-[#FAF7EE] cursor-help"
        >
          Pendiente
        </Badge>
      );
      const cd = patient.nextSessionDate ? getPaymentCountdown(new Date(patient.nextSessionDate)) : null;
      if (!cd) return badge;
      return (
        <Tooltip delayDuration={150}>
          <TooltipTrigger asChild>
            <span className="inline-flex">{badge}</span>
          </TooltipTrigger>
          <TooltipContent
            data-testid="patient-payment-countdown-tooltip"
            side="top"
            sideOffset={6}
            className="w-[260px] py-2.5 space-y-1 text-left leading-relaxed"
          >
            <p className="font-medium text-foreground text-[0.8125rem]">
              {cd.isExpired ? "Plazo de pago vencido" : cd.countdownLabel}
            </p>
            {!cd.isExpired && (
              <p className="text-xs text-muted-foreground">{cd.cancellationLabel}</p>
            )}
          </TooltipContent>
        </Tooltip>
      );
    }
    if (patient.paymentStatus === "paid") {
      return (
        <Badge
          data-testid="patient-payment-status-badge"
          className="bg-emerald-500 hover:bg-emerald-500 text-white rounded-full px-3 py-1 text-xs font-medium"
        >
          Pagado
        </Badge>
      );
    }
    return <span className="text-muted-foreground text-sm">—</span>;
  };

  const urgentMenuItem = (patient: Patient) => {
    if (patient.isUrgent) {
      return (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid="urgent-menu-item"
            className="text-red-500 focus:text-red-500 focus:bg-red-50"
            onClick={() => setUrgentConfirm({ patient, action: 'unmark' })}
          >
            <span>Desmarcar urgente</span>
          </DropdownMenuItem>
        </>
      );
    }
    return (
    <>
      <DropdownMenuSeparator />
      <DropdownMenuItem
        data-testid="urgent-menu-item"
        className="text-red-500 focus:text-red-500 focus:bg-red-50"
        onClick={() => {
          setMarkConditions(URGENT_CONDITIONS.map(() => false));
          setUrgentConfirm({ patient, action: 'mark' });
        }}
      >
        <span className="flex items-center justify-between w-full gap-1.5">
          <span>Marcar como urgente</span>
          {(
            <Tooltip delayDuration={150}>
              <TooltipTrigger asChild>
                <span
                  data-testid="urgent-info-btn"
                  aria-label="Más información sobre qué implica marcar como urgente"
                  className="inline-flex items-center text-red-400 hover:text-red-600 flex-shrink-0"
                  onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
                  onPointerDown={(e) => e.stopPropagation()}
                  onPointerUp={(e) => e.stopPropagation()}
                >
                  <Info size={13} aria-hidden="true" />
                </span>
              </TooltipTrigger>
              <TooltipContent
                side="left"
                sideOffset={8}
                className="w-[300px] py-3 space-y-2 text-left leading-relaxed"
              >
                <p className="font-semibold text-foreground text-[0.8125rem]">
                  ¿Qué significa marcar un paciente como urgente?
                </p>
                <p>
                  Indica que el paciente está atravesando una situación que requiere atención inmediata y prioridad clínica. Al marcarlo como urgente:
                </p>
                <ul className="list-disc pl-4 space-y-0.5">
                  {URGENT_CONDITIONS.map((condition) => (
                    <li key={condition}>{condition}</li>
                  ))}
                </ul>
                <p className="italic text-foreground/70">
                  Ejemplo: Un paciente que comunica estar en crisis emocional aguda y necesita ser atendido en las próximas horas.
                </p>
                <p className="text-foreground/70">
                  Usá esta opción con criterio clínico. El equipo de CX recibe un aviso cuando hay varios casos urgentes activos.
                </p>
              </TooltipContent>
            </Tooltip>
          )}
        </span>
      </DropdownMenuItem>
    </>
    );
  };

  const renderNameCell = (patient: Patient, showEmail = true) => (
    <TableCell className="border-r border-primary/20">
      <div className="flex items-center gap-2 flex-wrap">
        {patient.isUrgent && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                data-testid="urgent-dot"
                aria-label="Urgente"
                className="inline-flex w-2 h-2 rounded-full bg-red-500 flex-shrink-0 cursor-default"
              />
            </TooltipTrigger>
            <TooltipContent>Paciente marcado como urgente por psico</TooltipContent>
          </Tooltip>
        )}
        <button
          type="button"
          className="text-primary hover:underline font-medium text-left"
          onClick={() => onViewPatient?.(patient)}
        >
          {patient.name}
        </button>
        {patient.isReferred && (
          <span
            title="El paciente tuvo otra psicóloga anteriormente"
            className="inline-flex items-center px-2 py-0.5 rounded-full text-[0.75rem] font-medium bg-blue-50 text-blue-600 border border-blue-200 leading-none cursor-pointer shadow transition-transform hover:scale-[1.025]"
            onClick={(e) => { e.stopPropagation(); onViewPatientDerivation?.(patient); }}
          >
            Derivado
          </span>
        )}
      </div>
      {showEmail && patient.email && (
        <a
          href={buildGmailComposeUrl(patient.email, user?.email)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-muted-foreground mt-0.5 hover:text-primary transition-colors block"
          onClick={(e) => e.stopPropagation()}
        >
          {patient.email}
        </a>
      )}
    </TableCell>
  );

  // Read-only next session display (no "Agendar" button — for tabs where scheduling is disabled)
  const nextSessionDisplay = (patient: Patient) => {
    if (!patient.nextSession) return <span className="text-muted-foreground text-sm">—</span>;
    return (
      <button
        type="button"
        onClick={() => onGoToCalendar?.(patient)}
        title="Ver en agenda"
        className="inline-flex items-center gap-1.5 bg-violet-50 text-violet-700 text-xs font-medium px-2.5 py-1 rounded-full border border-violet-200 hover:bg-violet-100 hover:border-violet-300 transition-colors whitespace-nowrap cursor-pointer"
      >
        <Calendar size={11} />
        {patient.nextSession}
      </button>
    );
  };

  // CSS class to hide a column on mobile
  const hide = isMobile ? "hidden" : "";

  const renderHeaders = () => {
    const br = "border-r border-primary/20";
    if (activeTab === "all") {
      return (
        <>
          {sortHead("name", "Nombre", br)}
          <TableHead
            className={`text-primary font-semibold cursor-pointer select-none hover:bg-primary/5 transition-colors ${br}`}
            onClick={() => toggleSort("patientStatus")}
          >
            <span className="inline-flex items-center">
              Estado
              {sortIcon("patientStatus")}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setStatusInfoOpen(true); }}
                title="Ver qué significa cada estado"
                className="inline-flex items-center ml-1 text-primary hover:opacity-70"
              >
                <Info size={13} className="opacity-70" />
              </button>
            </span>
          </TableHead>
          <TableHead className={`text-primary font-semibold ${br}`}>Estado de pago</TableHead>
          <TableHead className={`text-primary font-semibold ${br} ${hide}`}>Tipo de proceso</TableHead>
          <TableHead className={`text-primary font-semibold ${br} ${hide}`}>Próxima sesión</TableHead>
          {sortHead("totalSessions", "Total sesiones", `text-center ${br} ${hide}`)}
          {sortHead("lastSession", "Última sesión", `${br} ${hide}`)}
          <TableHead className="text-primary font-semibold">Acciones</TableHead>
        </>
      );
    }
    if (activeTab === "lead") {
      return (
        <>
          {sortHead("name", "Nombre", br)}
          <TableHead className={`text-primary font-semibold ${br} ${hide}`}>Nacionalidad</TableHead>
          <TableHead className={`text-primary font-semibold ${br}`}>Estado de pago</TableHead>
          <TableHead className={`text-primary font-semibold ${br} ${hide}`}>Tipo de proceso</TableHead>
          <TableHead className={`text-primary font-semibold ${br} ${hide}`}>Próxima sesión</TableHead>
          <TableHead className={`text-primary font-semibold ${br} ${hide}`}>Motivo de consulta</TableHead>
          <TableHead className="text-primary font-semibold">Acciones</TableHead>
        </>
      );
    }
    if (activeTab === "active") {
      return (
        <>
          {sortHead("name", "Nombre", br)}
          <TableHead className={`text-primary font-semibold ${br} ${hide}`}>Nacionalidad</TableHead>
          <TableHead className={`text-primary font-semibold ${br} ${hide}`}>Tipo de proceso</TableHead>
          {sortHead("firstSession", "Primera sesión", `${br} ${hide}`)}
          {sortHead("lastSession", "Última sesión", `${br} ${hide}`)}
          {sortHead("nextSession", "Próxima sesión", br)}
          {sortHead("totalSessions", "Total sesiones", `text-center ${br} ${hide}`)}
          <TableHead className={`text-primary font-semibold ${br}`}>Estado de pago</TableHead>
          <TableHead className="text-primary font-semibold">Acciones</TableHead>
        </>
      );
    }
    // inactive
    return (
      <>
        {sortHead("name", "Nombre", br)}
        <TableHead className={`text-primary font-semibold ${br} ${hide}`}>Tipo de proceso</TableHead>
        {sortHead("lastSession", "Última sesión", br)}
        {sortHead("totalSessions", "Total sesiones", `text-center ${br} ${hide}`)}
        <TableHead className="text-primary font-semibold">Acciones</TableHead>
      </>
    );
  };

  const renderRow = (patient: Patient) => {
    const br = "border-r border-primary/20";
    if (activeTab === "all") {
      return (
        <>
          {/* El email debajo del nombre solo se muestra para pacientes Activos — para el
              resto de estados (Nuevo, En pausa, Inactivo, Derivación exitosa) no aporta
              valor de seguimiento y se oculta. */}
          {renderNameCell(patient, patient.patientStatus === "active")}
          <TableCell className={br}>{statusCell(patient)}</TableCell>
          <TableCell className={br}>{paymentStatusCell(patient)}</TableCell>
          <TableCell className={`text-muted-foreground text-sm ${br} ${hide}`}>{patient.service || "—"}</TableCell>
          <TableCell className={`${br} ${hide}`}>{nextSessionChip(patient)}</TableCell>
          <TableCell className={`text-center ${br} ${hide}`}>{totalSessionsBadge(patient.totalSessions)}</TableCell>
          <TableCell className={`text-muted-foreground text-sm ${br} ${hide}`}>{patient.totalSessions > 0 ? (patient.lastSession || "—") : "—"}</TableCell>
          <TableCell>
            {(() => {
              // Sin menú "..." para: procesos "Nuevo" (todavía no tienen acciones de
              // seguimiento), "En pausa" y "Derivación exitosa" (sin acciones de
              // seguimiento mientras dure ese estado) y procesos "Inactivo" que ya están
              // fríos (más de 60 días sin sesión — mismo cálculo que decide el chip
              // "Agendar" de "Próxima sesión"). Inactivo con sesión reciente (<=60 días)
              // SÍ mantiene el menú: "Agendar una sesión" sigue siendo una acción válida ahí.
              const hideActionsMenu =
                patient.patientStatus === "lead" ||
                patient.patientStatus === "on_hold" ||
                patient.patientStatus === "transferred" ||
                isStaleInactiveProcess(patient);
              return (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => onViewPatient?.(patient)}
                className={
                  hideActionsMenu
                    // Sin el "..." como alternativa, "Ver perfil" queda como único botón y
                    // siempre visible (no gateado por xl:).
                    ? "inline-flex border-primary text-primary hover:bg-primary/5 rounded-full px-[0.875rem] h-[35px] text-[0.8125rem] font-medium"
                    : "hidden xl:inline-flex border-primary text-primary hover:bg-primary/5 rounded-full px-[0.875rem] h-[35px] text-[0.8125rem] font-medium"
                }
              >
                Ver perfil
              </Button>
              {!hideActionsMenu && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      aria-label="Más acciones"
                      className="border-primary/30 text-primary hover:bg-primary/5 rounded-full h-[35px] w-[35px] p-0"
                    >
                      <MoreHorizontal size={16} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    collisionPadding={isMobile ? { bottom: 80 } : undefined}
                    style={isMobile ? { maxHeight: 'calc(100svh - 140px)', overflowY: 'auto' } : undefined}
                  >
                    <DropdownMenuItem onClick={() => onViewPatient?.(patient)}>
                      Ver perfil
                    </DropdownMenuItem>
                    {patient.patientStatus === "inactive" ? (
                      <>
                        {patient.schedulingEligible !== false && (
                          <DropdownMenuItem onClick={() => onScheduleFromList?.(patient)}>
                            Agendar una sesión
                          </DropdownMenuItem>
                        )}
                        {patient.totalSessions > 0 && (
                          <DropdownMenuItem onClick={() => onViewNotes?.(patient)}>
                            Ver notas de tus sesiones
                          </DropdownMenuItem>
                        )}
                      </>
                    ) : (
                      <>
                        <DropdownMenuItem onClick={() => onAddNote?.(patient)}>
                          Agregar nota de tu última sesión
                        </DropdownMenuItem>
                        {patient.totalSessions > 0 && (
                          <DropdownMenuItem onClick={() => onViewNotes?.(patient)}>
                            Ver notas de tus sesiones
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => onOpenNoSchedule?.(patient)}>
                          Formulario de no agendamiento
                        </DropdownMenuItem>
                        {disableReferForm ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <DropdownMenuItem
                                disabled
                                className="text-muted-foreground opacity-50 cursor-not-allowed"
                                onSelect={(e) => e.preventDefault()}
                              >
                                Formulario de derivación
                              </DropdownMenuItem>
                            </TooltipTrigger>
                            <TooltipContent>{REFER_DISABLED_TOOLTIP}</TooltipContent>
                          </Tooltip>
                        ) : (
                          <DropdownMenuItem onClick={() => onOpenRefer?.(patient)}>
                            Formulario de derivación
                          </DropdownMenuItem>
                        )}
                      </>
                    )}
                    {urgentMenuItem(patient)}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
              );
            })()}
          </TableCell>
        </>
      );
    }
    if (activeTab === "lead") {
      return (
        <>
          {renderNameCell(patient, false)}
          <TableCell className={`text-muted-foreground ${br} ${hide}`}>{patient.nationality || "—"}</TableCell>
          <TableCell className={br}>{paymentStatusCell(patient)}</TableCell>
          <TableCell className={`text-muted-foreground text-sm ${br} ${hide}`}>{patient.service || "—"}</TableCell>
          <TableCell className={`${br} ${hide}`}>{nextSessionDisplay(patient)}</TableCell>
          <TableCell className={`${br} ${hide}`}>
            <Button
              onClick={() => onReadConsultation?.(patient.consultationReason, patient)}
              className="bg-primary hover:bg-primary/90 text-white rounded-full px-[0.875rem] h-[35px] text-[0.8125rem] font-medium"
            >
              Leer consulta
            </Button>
          </TableCell>
          <TableCell>
            <div className="flex items-center gap-2">
              {!isMobile && (
                <Button
                  variant="outline"
                  onClick={() => onViewPatient?.(patient)}
                  className="border-primary text-primary hover:bg-primary/5 rounded-full px-[0.875rem] h-[35px] text-[0.8125rem] font-medium"
                >
                  Ver paciente
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    aria-label="Más acciones"
                    className="border-primary/30 text-primary hover:bg-primary/5 rounded-full h-[35px] w-[35px] p-0"
                  >
                    <MoreHorizontal size={16} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  collisionPadding={isMobile ? { bottom: 80 } : undefined}
                  style={isMobile ? { maxHeight: 'calc(100svh - 140px)', overflowY: 'auto' } : undefined}
                >
                  {isMobile && (
                    <DropdownMenuItem onClick={() => onViewPatient?.(patient)}>
                      Ver paciente
                    </DropdownMenuItem>
                  )}
                  {isMobile && (
                    <DropdownMenuItem onClick={() => onReadConsultation?.(patient.consultationReason, patient)}>
                      Leer consulta
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => onAddNote?.(patient)}>
                    Notas
                  </DropdownMenuItem>
                  {urgentMenuItem(patient)}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </TableCell>
        </>
      );
    }

    if (activeTab === "active") {
      return (
        <>
          {renderNameCell(patient)}
          <TableCell className={`text-muted-foreground text-sm ${br} ${hide}`}>{patient.nationality || "—"}</TableCell>
          <TableCell className={`text-muted-foreground text-sm ${br} ${hide}`}>{patient.service || "—"}</TableCell>
          <TableCell className={`text-muted-foreground text-sm ${br} ${hide}`}>{patient.firstSession || "—"}</TableCell>
          <TableCell className={`text-muted-foreground text-sm ${br} ${hide}`}>{patient.totalSessions > 0 ? (patient.lastSession || "—") : "—"}</TableCell>
          <TableCell className={br}>{nextSessionChip(patient)}</TableCell>
          <TableCell className={`text-center ${br} ${hide}`}>{totalSessionsBadge(patient.totalSessions)}</TableCell>
          <TableCell className={br}>{paymentStatusCell(patient)}</TableCell>
          <TableCell className="w-[1%] whitespace-nowrap">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => onViewPatient?.(patient)}
                className="hidden xl:inline-flex border-primary text-primary hover:bg-primary/5 rounded-full px-[0.875rem] h-[35px] text-[0.8125rem] font-medium whitespace-nowrap"
              >
                Ver perfil
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    aria-label="Más acciones"
                    className="border-primary/30 text-primary hover:bg-primary/5 rounded-full h-[35px] w-[35px] p-0"
                  >
                    <MoreHorizontal size={16} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  collisionPadding={isMobile ? { bottom: 80 } : undefined}
                  style={isMobile ? { maxHeight: 'calc(100svh - 140px)', overflowY: 'auto' } : undefined}
                >
                  <DropdownMenuItem onClick={() => onViewPatient?.(patient)}>
                    Ver perfil
                  </DropdownMenuItem>
                  {disableReferForm ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <DropdownMenuItem
                          disabled
                          className="text-muted-foreground opacity-50 cursor-not-allowed"
                          onSelect={(e) => e.preventDefault()}
                        >
                          Formulario de derivación
                        </DropdownMenuItem>
                      </TooltipTrigger>
                      <TooltipContent>{REFER_DISABLED_TOOLTIP}</TooltipContent>
                    </Tooltip>
                  ) : (
                    <DropdownMenuItem onClick={() => onOpenRefer?.(patient)}>
                      Formulario de derivación
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => onAddNote?.(patient)}>
                    Agregar nota de tu última sesión
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onViewNotes?.(patient)}>
                    Ver notas de tus sesiones
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onOpenNoSchedule?.(patient)}>
                    Formulario de no agendamiento
                  </DropdownMenuItem>
                  {urgentMenuItem(patient)}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </TableCell>
        </>
      );
    }

    // inactive
    return (
      <>
        {renderNameCell(patient, false)}
        <TableCell className={`text-muted-foreground text-sm ${br} ${hide}`}>{patient.service || "—"}</TableCell>
        <TableCell className={`text-muted-foreground text-sm ${br}`}>{patient.totalSessions > 0 ? (patient.lastSession || "—") : "—"}</TableCell>
        <TableCell className={`text-center ${br} ${hide}`}>{totalSessionsBadge(patient.totalSessions)}</TableCell>
        <TableCell className="w-[1%] whitespace-nowrap">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => onViewPatient?.(patient)}
              className="hidden xl:inline-flex border-primary text-primary hover:bg-primary/5 rounded-full px-[0.875rem] h-[35px] text-[0.8125rem] font-medium whitespace-nowrap"
            >
              Ver paciente
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  aria-label="Más acciones"
                  className="border-primary/30 text-primary hover:bg-primary/5 rounded-full h-[35px] w-[35px] p-0"
                >
                  <MoreHorizontal size={16} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onViewPatient?.(patient)}>
                  Ver paciente
                </DropdownMenuItem>
                {patient.schedulingEligible !== false && (
                  <DropdownMenuItem onClick={() => onScheduleFromList?.(patient)}>
                    Agendar una sesión
                  </DropdownMenuItem>
                )}
                {patient.totalSessions > 0 && (
                  <DropdownMenuItem onClick={() => onViewNotes?.(patient)}>
                    Ver notas de tus sesiones
                  </DropdownMenuItem>
                )}
                {urgentMenuItem(patient)}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </TableCell>
      </>
    );
  };

  return (
    <div className="rounded-xl border-2 border-primary/20 bg-card overflow-hidden">
      <div className="relative">
      <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-card hover:bg-card border-b-2 border-primary/20">
            {renderHeaders()}
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading && [...Array(6)].map((_, i) => (
            <TableRow key={i} className="border-b border-primary/10">
              {[...Array(colCount)].map((_, j) => (
                <TableCell key={j} className={j < colCount - 1 ? "border-r border-primary/10 py-3" : "py-3"}>
                  {j === 0 ? (
                    <>
                      <div className="h-4 skeleton-shimmer rounded w-28 mb-1.5" />
                      <div className="h-3 skeleton-shimmer rounded w-36" />
                    </>
                  ) : j === colCount - 1 ? (
                    <div className="w-8 h-8 skeleton-shimmer rounded" />
                  ) : j === 1 ? (
                    <div className="h-5 w-16 skeleton-shimmer rounded-full" />
                  ) : (
                    <div className="h-4 skeleton-shimmer rounded w-20" />
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
          {!loading && pagePatients.length === 0 && (
            <TableRow>
              <TableCell colSpan={colCount} className="py-10">
                <EmptyState
                  title="Sin pacientes"
                  description="No hay pacientes que coincidan con tu búsqueda o filtros."
                  showAction={false}
                  noBorder
                />
              </TableCell>
            </TableRow>
          )}
          {!loading &&
            pagePatients.map((patient) => (
              <TableRow
                key={patient.id}
                className="border-b border-primary/10 hover:bg-primary/[0.03] transition-colors"
              >
                {renderRow(patient)}
              </TableRow>
            ))}
        </TableBody>
      </Table>
      </div>
      {/* Fade del borde derecho — solo mobile, avisa que hay columnas fuera de la vista
          (varias columnas se ocultan en mobile pero las visibles igual pueden exceder el
          ancho de pantalla y requieren scroll horizontal dentro de la tabla). */}
      {isMobile && (
        <div className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-card to-transparent" />
      )}
      </div>

      {!loading && sortedPatients.length > 0 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-primary/10 bg-card">
          <span className="text-sm text-muted-foreground">
            Mostrando {startIndex + 1}–{Math.min(startIndex + itemsPerPage, sortedPatients.length)} de{" "}
            {sortedPatients.length} {sortedPatients.length === 1 ? "paciente" : "pacientes"}
          </span>
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                disabled={currentPage === 1}
                onClick={() => onPageChange(currentPage - 1)}
                className="h-8 w-8 p-0 rounded-full"
              >
                <ChevronLeft size={16} />
              </Button>
              <span className="text-sm text-muted-foreground px-2">
                {currentPage} / {totalPages}
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={currentPage >= totalPages}
                onClick={() => onPageChange(currentPage + 1)}
                className="h-8 w-8 p-0 rounded-full"
              >
                <ChevronRight size={16} />
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Estados de un proceso — misma info que el panel lateral de "Tasa de inactividad" */}
      <Dialog open={statusInfoOpen} onOpenChange={setStatusInfoOpen}>
        <DialogContent className="sm:max-w-2xl">
          <div className="px-6 pt-5 pb-6">
            <DialogHeader>
              <DialogTitle>Estados de un proceso</DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              <ProcessStatesInfo />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Urgent confirmation dialog */}
      {urgentConfirm && (
        <Dialog open onOpenChange={() => setUrgentConfirm(null)}>
          <DialogContent className="sm:max-w-sm">
            <div className="px-6 pt-5 pb-6">
            <DialogHeader>
              <DialogTitle>
                {urgentConfirm.action === 'mark' ? 'Marcar como urgente' : 'Desmarcar urgente'}
              </DialogTitle>
            </DialogHeader>
            {urgentConfirm.action === 'mark' ? (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  ¿Querés marcar a {urgentConfirm.patient.name} como paciente urgente? Confirmá que entendés y aceptás cada punto:
                </p>
                <div className="space-y-3 rounded-lg border border-primary/15 bg-primary/[0.03] px-4 py-3.5">
                  {URGENT_CONDITIONS.map((condition, idx) => (
                    <label
                      key={condition}
                      htmlFor={`urgent-condition-${idx}`}
                      className="flex items-start gap-2.5 text-sm text-foreground cursor-pointer select-none"
                    >
                      <input
                        id={`urgent-condition-${idx}`}
                        data-testid={`urgent-condition-checkbox-${idx}`}
                        type="checkbox"
                        checked={markConditions[idx]}
                        onChange={(e) =>
                          setMarkConditions((prev) => prev.map((v, i) => (i === idx ? e.target.checked : v)))
                        }
                        className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 accent-primary cursor-pointer"
                      />
                      <span className="leading-snug">{condition}</span>
                    </label>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                ¿Querés desmarcar a {urgentConfirm.patient.name} como paciente urgente?
              </p>
            )}
            <div className="flex justify-end gap-2 pt-5">
              <Button variant="outline" onClick={() => setUrgentConfirm(null)}>
                Cancelar
              </Button>
              <Button
                data-testid="urgent-confirm-btn"
                disabled={urgentConfirm.action === 'mark' && !markConditions.every(Boolean)}
                onClick={() => {
                  if (urgentConfirm.action === 'mark') {
                    onMarkUrgent?.(urgentConfirm.patient);
                  } else {
                    onUnmarkUrgent?.(urgentConfirm.patient);
                  }
                  setUrgentConfirm(null);
                }}
              >
                Confirmar
              </Button>
            </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
